/**
 * All doc writes go through here (spec §10): validate, mutate, commit with a message. No component
 * touches containers directly. Each op is one Loro commit = one undo step.
 */
import { LoroList, LoroMap, LoroMovableList, LoroText, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "./actor";
import { readTask, taskMap, tasksMap, tree } from "./tasks";
import { C, type PropDef, type PropVal } from "./schema";
import type { QuickAddResult } from "./quickadd";
import { Snapshot, validateEdge, wouldCreateTreeCycle, type EdgeKind } from "./structure";
import {
  fairnessOf,
  partEntry,
  pickPart,
  roundOf,
  rotates,
  ROTATE,
  type Fairness,
  type Round,
} from "./parts";

export interface CreateTaskInput {
  title: string;
  typeId?: string;
  notes?: string;
  parentId?: string | null;
  props?: Record<string, PropVal>;
  id?: string;
  now?: Date;
}

export function createTask(doc: LoroDoc, input: CreateTaskInput): string {
  const title = input.title.trim();
  if (!title && input.typeId !== "note") throw new Error("ops.emptyTitle");
  const id = input.id ?? ulid();
  const now = (input.now ?? new Date()).toISOString();
  const t = tasksMap(doc).setContainer(id, new LoroMap());
  t.set("id", id);
  t.set("typeId", input.typeId ?? "task");
  t.setContainer("title", new LoroText()).insert(0, title);
  t.setContainer("notes", new LoroText()).insert(0, input.notes ?? "");
  t.set("createdAt", now);
  t.set("doneAt", null);
  t.set("archivedAt", null);
  t.set("orderedChildren", false);
  const props = t.setContainer("props", new LoroMap());
  for (const [k, v] of Object.entries(input.props ?? {})) props.set(k, v);
  t.setContainer("attachments", new LoroMap());
  // tree node (ADR 0003)
  const parentNode = input.parentId ? taskMap(doc, input.parentId)?.get("nodeId") : undefined;
  const node = tree(doc).createNode(
    typeof parentNode === "string" ? (parentNode as `${number}@${number}`) : undefined,
  );
  node.data.set("taskId", id);
  t.set("nodeId", node.id);
  doc.commit({ message: commitMessage({ op: "createTask" }) });
  return id;
}

function replaceText(text: LoroText, next: string): void {
  const cur = text.toString();
  if (cur === next) return;
  // minimal diff: common prefix/suffix, so concurrent edits elsewhere in the text survive
  let a = 0;
  while (a < cur.length && a < next.length && cur[a] === next[a]) a++;
  let b = 0;
  while (b < cur.length - a && b < next.length - a && cur[cur.length - 1 - b] === next[next.length - 1 - b])
    b++;
  if (cur.length - a - b > 0) text.delete(a, cur.length - a - b);
  if (next.length - a - b > 0) text.insert(a, next.slice(a, next.length - b));
}

export function setTitle(doc: LoroDoc, id: string, title: string): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  replaceText(t.get("title") as LoroText, title);
  doc.commit({ message: commitMessage({ op: "setTitle" }) });
}

export function setNotes(doc: LoroDoc, id: string, notes: string): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  replaceText(t.get("notes") as LoroText, notes);
  doc.commit({ message: commitMessage({ op: "setNotes" }) });
}

export function markDone(doc: LoroDoc, id: string, opts: { now?: Date; doneBy?: string } = {}): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  t.set("doneAt", (opts.now ?? new Date()).toISOString());
  if (opts.doneBy) t.set("doneBy", opts.doneBy);
  (t.get("props") as LoroMap).delete(SET_ASIDE_COUNT);
  doc.commit({ message: commitMessage({ op: "markDone" }) });
}

export function markUndone(doc: LoroDoc, id: string): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  t.set("doneAt", null);
  t.delete("doneBy");
  doc.commit({ message: commitMessage({ op: "markUndone" }) });
}

/** Soft delete (spec §9.3): archives the task and its descendants; never hard-deletes. */
export function archiveTask(doc: LoroDoc, id: string, now = new Date()): void {
  const stamp = now.toISOString();
  const visit = (tid: string) => {
    const t = taskMap(doc, tid);
    if (!t) return;
    if (!t.get("archivedAt")) t.set("archivedAt", stamp);
    const nodeId = t.get("nodeId");
    if (typeof nodeId === "string") {
      for (const child of tree(doc)
        .getNodeByID(nodeId as `${number}@${number}`)
        ?.children() ?? []) {
        const cid = child.data.get("taskId");
        if (typeof cid === "string") visit(cid);
      }
    }
  };
  visit(id);
  doc.commit({ message: commitMessage({ op: "archiveTask" }) });
}

export function unarchiveTask(doc: LoroDoc, id: string): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  t.set("archivedAt", null);
  doc.commit({ message: commitMessage({ op: "unarchiveTask" }) });
}

export function setProp(doc: LoroDoc, id: string, propId: string, value: PropVal | null): void {
  const t = taskMap(doc, id);
  if (!t) throw new Error("ops.noSuchTask");
  const props = t.get("props") as LoroMap<Record<string, PropVal>>;
  if (value === null || value === undefined) props.delete(propId);
  else props.set(propId, value);
  doc.commit({ message: commitMessage({ op: "setProp", propId }) });
}

// --- structure (spec §9.3) -----------------------------------------------------------------

export function snapshotOf(doc: LoroDoc): Snapshot {
  return new Snapshot(doc.toJSON() as never);
}

export class OpError extends Error {
  constructor(
    public code: string,
    public warning?: string,
  ) {
    super(code);
  }
}

/** Adds a `depends` or `relates` edge after validation; returns {edgeId, warning?}. Rejects cycles. */
export function addEdge(
  doc: LoroDoc,
  kind: EdgeKind,
  from: string,
  to: string,
): { edgeId: string; warning?: string } {
  const v = validateEdge(snapshotOf(doc), kind, from, to);
  if (!v.ok) throw new OpError(v.error ?? "structure.invalid");
  const edges = doc.getMap(C.edges);
  for (const [eid, e] of Object.entries(
    edges.toJSON() as Record<string, { kind: string; from: string; to: string }>,
  )) {
    if (
      e.kind === kind &&
      ((e.from === from && e.to === to) || (kind === "relates" && e.from === to && e.to === from))
    )
      return { edgeId: eid, warning: v.warning };
  }
  const edgeId = ulid();
  const m = edges.setContainer(edgeId, new LoroMap());
  m.set("kind", kind);
  m.set("from", from);
  m.set("to", to);
  doc.commit({ message: commitMessage({ op: "addEdge", kind }) });
  return { edgeId, warning: v.warning };
}

export function removeEdge(doc: LoroDoc, edgeId: string): void {
  doc.getMap(C.edges).delete(edgeId);
  doc.commit({ message: commitMessage({ op: "removeEdge" }) });
}

export function createSequence(
  doc: LoroDoc,
  title: string,
  items: string[] = [],
  opts: { routine?: boolean } = {},
): string {
  const id = ulid();
  const m = doc.getMap(C.sequences).setContainer(id, new LoroMap());
  m.set("id", id);
  m.set("title", title);
  if (opts.routine) m.set("routine", true);
  const list = m.setContainer("items", new LoroMovableList());
  items.forEach((t, i) => list.insert(i, t));
  doc.commit({ message: commitMessage({ op: "createSequence" }) });
  return id;
}

function seqItems(doc: LoroDoc, seqId: string): LoroMovableList<string> {
  const m = doc.getMap(C.sequences).get(seqId);
  if (!m || typeof m !== "object" || !("get" in m)) throw new OpError("structure.noSuchSequence");
  return (m as LoroMap).get("items") as LoroMovableList<string>;
}

export function addToSequence(doc: LoroDoc, seqId: string, taskId: string, index?: number): void {
  const list = seqItems(doc, seqId);
  const cur = list.toArray();
  if (cur.includes(taskId)) return;
  list.insert(index ?? cur.length, taskId);
  doc.commit({ message: commitMessage({ op: "addToSequence" }) });
}

export function removeFromSequence(doc: LoroDoc, seqId: string, taskId: string): void {
  const list = seqItems(doc, seqId);
  const i = list.toArray().indexOf(taskId);
  if (i < 0) return;
  list.delete(i, 1);
  doc.commit({ message: commitMessage({ op: "removeFromSequence" }) });
}

export function moveInSequence(doc: LoroDoc, seqId: string, taskId: string, toIndex: number): void {
  const list = seqItems(doc, seqId);
  const i = list.toArray().indexOf(taskId);
  if (i < 0) throw new OpError("structure.notInSequence");
  list.move(i, Math.max(0, Math.min(toIndex, list.length - 1)));
  doc.commit({ message: commitMessage({ op: "moveInSequence" }) });
}

export function deleteSequence(doc: LoroDoc, seqId: string): void {
  doc.getMap(C.sequences).delete(seqId);
  doc.commit({ message: commitMessage({ op: "deleteSequence" }) });
}

export function createGroup(doc: LoroDoc, title: string, color: string | null = null): string {
  const id = ulid();
  const m = doc.getMap(C.groups).setContainer(id, new LoroMap());
  m.set("id", id);
  m.set("title", title);
  m.set("color", color);
  m.setContainer("members", new LoroMap());
  doc.commit({ message: commitMessage({ op: "createGroup" }) });
  return id;
}

function groupMembers(doc: LoroDoc, groupId: string): LoroMap {
  const m = doc.getMap(C.groups).get(groupId);
  if (!m || typeof m !== "object" || !("get" in m)) throw new OpError("structure.noSuchGroup");
  return (m as LoroMap).get("members") as LoroMap;
}

export function addToGroup(doc: LoroDoc, groupId: string, taskId: string): void {
  groupMembers(doc, groupId).set(taskId, true);
  doc.commit({ message: commitMessage({ op: "addToGroup" }) });
}

export function removeFromGroup(doc: LoroDoc, groupId: string, taskId: string): void {
  groupMembers(doc, groupId).delete(taskId);
  doc.commit({ message: commitMessage({ op: "removeFromGroup" }) });
}

export function updateGroup(
  doc: LoroDoc,
  groupId: string,
  patch: { title?: string; color?: string | null },
): void {
  const m = doc.getMap(C.groups).get(groupId) as LoroMap | undefined;
  if (!m) throw new OpError("structure.noSuchGroup");
  if (patch.title !== undefined) m.set("title", patch.title);
  if (patch.color !== undefined) m.set("color", patch.color);
  doc.commit({ message: commitMessage({ op: "updateGroup" }) });
}

export function deleteGroup(doc: LoroDoc, groupId: string): void {
  doc.getMap(C.groups).delete(groupId);
  doc.commit({ message: commitMessage({ op: "deleteGroup" }) });
}

/** Re-parent a task in the tree (spec §9.3); refuses to move a task under itself or a descendant. */
export function moveTask(doc: LoroDoc, taskId: string, newParentId: string | null, index?: number): void {
  const s = snapshotOf(doc);
  if (wouldCreateTreeCycle(s, taskId, newParentId)) throw new OpError("structure.treeCycle");
  const t = taskMap(doc, taskId);
  if (!t) throw new OpError("ops.noSuchTask");
  const nodeId = t.get("nodeId") as `${number}@${number}` | undefined;
  const tr = tree(doc);
  const node = nodeId ? tr.getNodeByID(nodeId) : undefined;
  if (!node) throw new OpError("structure.noNode");
  const parentNodeId = newParentId
    ? (taskMap(doc, newParentId)?.get("nodeId") as `${number}@${number}` | undefined)
    : undefined;
  if (newParentId && !parentNodeId) throw new OpError("ops.noSuchTask");
  node.move(parentNodeId ? tr.getNodeByID(parentNodeId) : undefined, index);
  doc.commit({ message: commitMessage({ op: "moveTask" }) });
}

export function setOrderedChildren(doc: LoroDoc, taskId: string, ordered: boolean): void {
  const t = taskMap(doc, taskId);
  if (!t) throw new OpError("ops.noSuchTask");
  t.set("orderedChildren", ordered);
  doc.commit({ message: commitMessage({ op: "setOrderedChildren" }) });
}

/**
 * How a task's subtasks are done: all at once, in order (the ordered checklist), or one per round (rotating subtasks,
 * ADR 0035). One choice, so the two can never both be on.
 */
export type SubtaskMode = "all" | "ordered" | "rotate";

export function setSubtaskMode(doc: LoroDoc, taskId: string, mode: SubtaskMode): void {
  const t = taskMap(doc, taskId);
  if (!t) throw new OpError("ops.noSuchTask");
  t.set("orderedChildren", mode === "ordered");
  const props = t.get("props") as LoroMap<Record<string, PropVal>>;
  if (mode === "rotate") props.set(ROTATE, true);
  else props.delete(ROTATE);
  doc.commit({ message: commitMessage({ op: "setSubtaskMode" }) });
}

export function setTaskType(doc: LoroDoc, taskId: string, typeId: string): void {
  const t = taskMap(doc, taskId);
  if (!t) throw new OpError("ops.noSuchTask");
  t.set("typeId", typeId);
  doc.commit({ message: commitMessage({ op: "setTaskType" }) });
}

// --- property definitions (spec §9.4) ------------------------------------------------------

export function createPropDef(doc: LoroDoc, def: Omit<PropDef, "id" | "builtin"> & { id?: string }): string {
  const id = def.id ?? ulid();
  const defs = doc.getMap(C.propDefs);
  const m = defs.setContainer(id, new LoroMap());
  m.set("id", id);
  m.set("name", def.name);
  m.set("kind", def.kind);
  m.set("spec", def.spec);
  m.set("builtin", false);
  m.set("shownInQuickAdd", def.shownInQuickAdd ?? true);
  m.set("level", def.level ?? "standard");
  if (def.bundleId) m.set("bundleId", def.bundleId);
  doc.commit({ message: commitMessage({ op: "createPropDef" }) });
  return id;
}

export function updatePropDef(
  doc: LoroDoc,
  id: string,
  patch: Partial<Pick<PropDef, "name" | "spec" | "shownInQuickAdd" | "level">>,
): void {
  const m = doc.getMap(C.propDefs).get(id) as LoroMap | undefined;
  if (!m) throw new OpError("props.noSuchDef");
  for (const [k, v] of Object.entries(patch)) if (v !== undefined) m.set(k, v as never);
  doc.commit({ message: commitMessage({ op: "updatePropDef" }) });
}

/** Builtin PropDefs are undeletable (spec §9.4). */
export function deletePropDef(doc: LoroDoc, id: string): void {
  const m = doc.getMap(C.propDefs).get(id) as LoroMap | undefined;
  if (!m) return;
  if (m.get("builtin")) throw new OpError("props.builtinUndeletable");
  doc.getMap(C.propDefs).delete(id);
  doc.commit({ message: commitMessage({ op: "deletePropDef" }) });
}

/** The property definitions the UI offers; `spec.hidden` ones are bookkeeping and never listed (ADR 0013). */
export function listPropDefs(doc: LoroDoc): PropDef[] {
  const all = doc.getMap(C.propDefs).toJSON() as Record<string, PropDef>;
  return Object.values(all).filter(
    (d) => d && typeof d === "object" && (d.spec as { hidden?: boolean } | undefined)?.hidden !== true,
  );
}

/** What quick-add may set: the listed definitions, and hidden ones that quick-add writes (`with:`, P2). */
export function quickAddPropDefs(doc: LoroDoc): PropDef[] {
  const all = doc.getMap(C.propDefs).toJSON() as Record<string, PropDef>;
  return Object.values(all).filter(
    (d) =>
      d &&
      typeof d === "object" &&
      ((d.spec as { hidden?: boolean } | undefined)?.hidden !== true || d.shownInQuickAdd === true),
  );
}

export function findTagDef(doc: LoroDoc, name: string): PropDef | undefined {
  const n = name.toLowerCase();
  return listPropDefs(doc).find((d) => d.kind === "tag" && d.name.toLowerCase() === n);
}

export function findGroupByTitle(doc: LoroDoc, title: string): string | undefined {
  const all = doc.getMap(C.groups).toJSON() as Record<string, { title?: string }>;
  const t = title.toLowerCase();
  return Object.entries(all).find(([, g]) => (g?.title ?? "").toLowerCase() === t)?.[0];
}

/** Turn a parsed quick-add into doc writes: tags → tag PropDefs, groups → groups, then the task. */
/** How place names compare (I9): case, spaces, hyphens and underscores do not matter. Mirror of `ops.py`. */
export const placeKey = (name: string): string =>
  name
    .replace(/[\s_-]+/g, " ")
    .trim()
    .toLowerCase();

/** The place with this name, else the place list with this name (a place wins over a list). Mirror of `ops.py`. */
export function findPlace(doc: LoroDoc, name: string): string | null {
  const settings = doc.getMap(C.settings).toJSON() as Record<
    string,
    Record<string, { name?: string }> | undefined
  >;
  const key = placeKey(name);
  for (const which of ["places", "placeLists"]) {
    const hit = Object.entries(settings[which] ?? {}).find(
      ([, p]) => placeKey(String(p?.name ?? "")) === key,
    );
    if (hit) return hit[0];
  }
  return null;
}

export function applyQuickAdd(
  doc: LoroDoc,
  parsed: QuickAddResult,
  opts: { parentId?: string | null; now?: Date } = {},
): string {
  const props: Record<string, PropVal> = { ...parsed.props };
  for (const tag of parsed.tags) {
    const def = findTagDef(doc, tag) ?? {
      id: createPropDef(doc, {
        name: tag,
        kind: "tag",
        spec: { color: null },
        shownInQuickAdd: true,
        level: "simple",
      }),
    };
    props[def.id] = true;
  }
  if (parsed.place) props.atPlace = findPlace(doc, parsed.place) ?? parsed.place;
  const id = createTask(doc, {
    title: parsed.title,
    typeId: parsed.typeId,
    notes: parsed.notes,
    parentId: opts.parentId ?? null,
    props,
    now: opts.now,
  });
  for (const title of parsed.groups) {
    const gid = findGroupByTitle(doc, title) ?? createGroup(doc, title);
    addToGroup(doc, gid, id);
  }
  return id;
}

// --- recurring (spec §9.5) --------------------------------------------------------------------

const HISTORY_CAP = 200;

type HistoryList = LoroList<Record<string, unknown>>;

function historyList(t: TaskMapLike): HistoryList {
  const props = t.get("props") as LoroMap;
  const existing = props.get("rec.history") as unknown;
  if (existing instanceof LoroList) return existing as HistoryList;
  // one shape (UI cleanup U3): a plain array (written with a fixed repeat's next occurrence) becomes the list,
  // its entries kept — before, it was replaced by an empty one
  const list = props.setContainer("rec.history", new LoroList()) as unknown as HistoryList;
  if (Array.isArray(existing)) for (const e of existing) list.push(e as Record<string, unknown>);
  return list;
}

type TaskMapLike = ReturnType<typeof taskMap> & object;

function pushHistory(t: TaskMapLike, entry: Record<string, unknown>): void {
  const list = historyList(t);
  list.push(entry);
  while (list.length > HISTORY_CAP) list.delete(0, 1);
}

/** Completing a recurring task: `lastDoneAt = now`, history entry, `doneAt` stays null (it never finishes). */
export function completeRecurring(
  doc: LoroDoc,
  id: string,
  opts: { now?: Date; by?: string; history?: boolean } = {},
): void {
  const t = taskMap(doc, id);
  if (!t) throw new OpError("ops.noSuchTask");
  const now = (opts.now ?? new Date()).toISOString();
  const props = t.get("props") as LoroMap;
  props.set("rec.lastDoneAt", now);
  props.delete(SET_ASIDE_COUNT);
  // a round of rotating subtasks has its parts in the history already (ADR 0035)
  if (opts.history !== false) pushHistory(t, opts.by ? { at: now, by: opts.by } : { at: now });
  doc.commit({ message: commitMessage({ op: "completeRecurring" }) });
}

/**
 * *Not now* (UI cleanup U3, `UI_ANALYSIS.md` §4.1a): out of sight until a return point, never out of the system. The
 * task's *Not before* (`startAt`) is the return point; a repeating task's want keeps growing meanwhile. How many
 * times in a row it was set aside is kept beside it, so the third time can ask once whether something would help;
 * done clears it. Mirror: `set_aside` in ops.py. Returns the count.
 */
export const SET_ASIDE_COUNT = "notNow.count";
export const SET_ASIDE_QUIET = "notNow.quietUntil";

export function setAside(doc: LoroDoc, id: string, until: Date): number {
  const t = taskMap(doc, id);
  if (!t) throw new OpError("ops.noSuchTask");
  const props = t.get("props") as LoroMap;
  props.set("startAt", until.toISOString());
  const n = props.get(SET_ASIDE_COUNT);
  const count = (typeof n === "number" ? n : 0) + 1;
  props.set(SET_ASIDE_COUNT, count);
  doc.commit({ message: commitMessage({ op: "setAside" }) });
  return count;
}

/** "Keep as is" after the question: no question about this task for `days` days. */
export function quietSetAside(doc: LoroDoc, id: string, until: Date): void {
  const t = taskMap(doc, id);
  if (!t) throw new OpError("ops.noSuchTask");
  (t.get("props") as LoroMap).set(SET_ASIDE_QUIET, until.toISOString());
  doc.commit({ message: commitMessage({ op: "quietSetAside" }) });
}

/** Skip: the clock restarts and history records `skipped: true`; only where turns are taken (the turn stays where it is). */
export function skipRecurring(doc: LoroDoc, id: string, opts: { now?: Date; by?: string } = {}): void {
  const t = taskMap(doc, id);
  if (!t) throw new OpError("ops.noSuchTask");
  const now = (opts.now ?? new Date()).toISOString();
  const props = t.get("props") as LoroMap;
  props.set("rec.lastDoneAt", now);
  pushHistory(t, { at: now, skipped: true, ...(opts.by ? { by: opts.by } : {}) });
  doc.commit({ message: commitMessage({ op: "skipRecurring" }) });
}

/** The open subtasks of a task in order: the parts of a task with rotating subtasks (ADR 0035). */
export function openChildren(s: Snapshot, id: string): string[] {
  return s.childrenOf(id).filter((c) => s.isOpen(c));
}

function circleOf(doc: LoroDoc): { shared: boolean; members: string[] | null; fair: Fairness } {
  const settings = doc.getMap(C.settings).toJSON() as {
    shared?: boolean;
    members?: { id?: string; kind?: string }[];
    fairness?: unknown;
  };
  const members = Array.isArray(settings.members)
    ? settings.members.filter((m) => m && m.kind !== "viewer" && m.id).map((m) => String(m.id))
    : [];
  return {
    shared: settings.shared === true,
    members: members.length ? members : null,
    fair: fairnessOf(settings),
  };
}

/** This round of a task with rotating subtasks, as the doc says now; null when it has none. */
export function readRound(doc: LoroDoc, id: string): Round | null {
  const task = readTask(doc, id);
  if (!task || !rotates(task)) return null;
  return roundOf(task, openChildren(snapshotOf(doc), id), circleOf(doc));
}

/** Every open task with rotating subtasks: its round, its open subtasks and their titles. */
export function readRounds(
  doc: LoroDoc,
): Record<string, { round: Round; subs: string[]; titles: Record<string, string>; fair: Fairness }> {
  const s = snapshotOf(doc);
  const circle = circleOf(doc);
  const out: Record<
    string,
    { round: Round; subs: string[]; titles: Record<string, string>; fair: Fairness }
  > = {};
  for (const [id, raw] of Object.entries(s.tasks)) {
    const task = { ...(raw as { props?: Record<string, unknown> }), id };
    if (!rotates(task) || !s.isOpen(id)) continue;
    const subs = openChildren(s, id);
    const round = roundOf(task, subs, circle);
    if (!round) continue;
    const titles = Object.fromEntries(subs.map((c) => [c, String(s.tasks[c]?.title ?? "")]));
    out[id] = { round, subs, titles, fair: circle.fair };
  }
  return out;
}

/**
 * One part of this round done (ADR 0035): `sub` names it, else it is the doer's part, else the first open one. Returns
 * whether that finished the round, or null when there was no open part. Mirror: `complete_part` in ops.py.
 */
export function completePart(
  doc: LoroDoc,
  id: string,
  opts: { now?: Date; by?: string; sub?: string | null } = {},
): boolean | null {
  const t = taskMap(doc, id);
  if (!t) throw new OpError("ops.noSuchTask");
  const round = readRound(doc, id);
  if (!round) return null;
  const profile = opts.by ? opts.by.replace(/^profile:/, "") : null;
  const part = pickPart(round, profile, opts.sub);
  if (!part) {
    if (opts.sub != null) throw new OpError("parts.notInRound");
    return null;
  }
  pushHistory(t, partEntry(round, part, (opts.now ?? new Date()).toISOString(), opts.by));
  doc.commit({ message: commitMessage({ op: "completePart" }) });
  return round.parts.every((x) => x.done || x.sub === part.sub);
}

export function readHistory(doc: LoroDoc, id: string): { at: string; by?: string; skipped?: boolean }[] {
  const t = readTask(doc, id);
  const h = t?.props["rec.history"] as unknown;
  return Array.isArray(h) ? (h as { at: string; by?: string; skipped?: boolean }[]) : [];
}
